import { CircularProgress } from "@material-ui/core";
import { attributes, Collection, Linked } from "@type-r/models";
import { useModel } from "@type-r/react";
import { matchString } from "api/common";
import { RowH2Bold, Chip } from "common";
import { EmptySpace, HeaderContainer, HeaderField } from "common/invoice-aging";
import { CompactTabs, Tab } from "lqx-react";
import { asCurrency, asDate, CompactSelect, div, Flex, FormCheckbox, Option, SearchInput, TilesList } from "lqx-react/src";
import React, { useMemo } from "react";
import { InvoiceItem } from "../api";
import { CustomerProfileLandingSummary } from "../api/customer-profile";
import { InvoiceLandingSummary } from "../api/invoices";
import { PaymentRaLandingSummary } from "../api/payments";

const GridState = attributes({
    filter: '',
    sortBy: {
        name: 'invoiceDueDate',
        isAsc: true
    }
})
type GridState = InstanceType<typeof GridState>;

const toMMDDYYYString = (date: Date) => date && `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;

const filterInvoices = (state: GridState) => (invoice: InvoiceItem): boolean =>
(
    !state.filter
    || state.filter.length < 3
    || invoice.invoiceNumber.indexOf(state.filter) >= 0
    || matchString(String(invoice.outstandingInvoiceAmount), state.filter)
    || matchString(asCurrency(invoice.outstandingInvoiceAmount), state.filter)
    || matchString(toMMDDYYYString(invoice.invoiceDueDate), state.filter)
);

export const InvoiceItemGrid = ({ invoiceItems, $selected }:
    { invoiceItems: Collection<InvoiceItem>, $selected: Linked<Collection<InvoiceItem>> }) => {

    const state = useModel(GridState)

    const sorted = useMemo(() => invoiceItems
        .filter(filterInvoices(state))
        .sort(InvoiceItem.comparator('invoiceDueDate', true))
        , [state.filter]);

    const sortedLength = sorted ? sorted.length : 0;

    return <>
        <Flex row>
            <FormCheckbox
                $checked={
                    Linked.value(
                        sortedLength ? invoiceItems.length === $selected.value.length : false,
                        x => x ? $selected.value.set(sorted) : $selected.value.reset()
                    )
                }
            />
            <SearchInput
                placeholder="Search by Invoice Number, Amount, Due Date"
                $value={state.$.filter} />
        </Flex>
        <InvoiceItemTileHeader />
        <TilesList
            items={sorted}
            itemSize={42}
            height={200}
            renderer={InvoiceItemTile($selected as any)}
            selected={undefined /* selected */}
        />
    </>
}

export const InvoiceItemTileHeader = () =>
    <Flex row>
        <EmptySpace width={48} height={40} />
        <HeaderContainer>
            <EmptySpace width={40} height={33} />
            <HeaderField width={148}>Invoice #</HeaderField>
            <HeaderField width={142}>Invoice Amt</HeaderField>
            <HeaderField width={176}>Due Date</HeaderField>
            <Flex grow />
        </HeaderContainer>
    </Flex>

export const InvoiceItemListField = div({
    display: 'flex',
    flexDirection: 'row',
    paddingLeft: 8,
    paddingRight: 8,
    minWidth: '12em',
    fontSize: 12,
    color: '#A0A0A0'
})

export const InvoiceItemTile = (selected: Linked<Collection<InvoiceItem>>) =>
    ({ data, style, index }) => {
        const invoice: InvoiceItem = data.items[index];

        return (
            <div style={style}>
                <Flex row alignCenter>
                    <FormCheckbox $checked={selected.value.$includes(invoice)} />
                    <InvoiceItemListField>
                        <RowH2Bold>{invoice.invoiceNumber}</RowH2Bold>
                    </InvoiceItemListField>
                    <InvoiceItemListField>
                        {asCurrency(invoice.outstandingInvoiceAmount, invoice.currency)}
                    </InvoiceItemListField>
                    <InvoiceItemListField>
                        {asDate(invoice.invoiceDueDate)}
                    </InvoiceItemListField>
                </Flex>
            </div>
        )
    }

export const SelectFilter = ({ $filter, model, type }: {
    $filter: Linked<string>
    model?: InvoiceLandingSummary | PaymentRaLandingSummary | CustomerProfileLandingSummary,
    type?: "invoices" | "payments" | "cp"
}) =>
    <CompactTabs $value={$filter}>
        <Tab value="todo">To do ({model ? (model.isTodoReady ? model.todoCount : ' loading ') : "-"})</Tab>
        {type && type == "cp" ? <></> : <Tab value="review">Review ({model ? (model.isReviewReady ? model.reviewCount : ' loading ') : "-"})</Tab>}
    </CompactTabs>

export const ToDo = () =>
    <Flex stretch center>
        <h2>TO BE CONSTRUCTED</h2>
    </Flex>

export const TimeRangeSelector = ({ $timeRange, timeRangeTypes }: { $timeRange: Linked<number>, timeRangeTypes: any }) =>
    <div style={{ maxWidth: 350 }}>
        <CompactSelect label="Time Range" $value={$timeRange}>
            {
                timeRangeTypes.map(option => <Option value={option.val}> {option.label} </Option>)
            }
        </CompactSelect>
    </div>

export const ChipWithLoading = ({ model, count, color }: { model, isReady?, count?, color?}) =>
    <Chip color={color ? color : 'yellow'}>{
        model.isReady
            ? (count ? count : model.getCount())
            : <WidgetSmallLoading />
    }
    </Chip>

export const WidgetSmallLoading = () =>
    <Flex center>
        <CircularProgress style={{ height: 16, width: 16, color: 'white', margin: 5 }} />
    </Flex>